import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Modal } from 'semantic-ui-react';
import { AlertTriangle, Clock, Star } from 'lucide-react';
import { MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';
import { StyledButton, StyledTrashButton } from 'common/components/button/StyledButton';

interface MobilePlannerTableProps {
  items: any[];
  isLoading?: boolean;
  onRemove: (identifier: string | number | object) => void;
  isMostRecent?: boolean;
}

const MobilePlannerTable: React.FC<MobilePlannerTableProps> = ({
  items,
  isLoading = false,
  onRemove,
  isMostRecent = true,
}) => {
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | number | null>(null);

  if (isLoading) {
    return (
      <LoadingWrapper>
        <MobileSpinner />
      </LoadingWrapper>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState>
        {isMostRecent ? <Star size={22} color="#10b981" /> : <Clock size={22} color="#94a3b8" />}
        <EmptyStateBody>
          <EmptyStateTitle>{isMostRecent ? 'Start Your Focus Plan' : 'No focus plan for this period'}</EmptyStateTitle>
          {isMostRecent ? (
            <EmptyStateDescription>
                Tap the Add to Focus Plan button to explore recommendations and add focus areas tailored to your team.
            </EmptyStateDescription>
          ) : (
            <EmptyStateDescription>No focus plan was created for this assessment period.</EmptyStateDescription>
          )}
        </EmptyStateBody>
      </EmptyState>
    );
  }

  return (
    <List>
      {items.map((item, index) => {
        const key = item.task_id ?? item.recommendation_template_id ?? `item-${index}`;
        const canViewDetails = typeof item.recommendation_template_id === 'number' && typeof item.task_index !== 'undefined';
        const itemTitle = item.task || item.task_title || item.title || 'Focus area';
        const itemDescription = item.task_description || item.description || 'Not specified';
        const hasTemplate = typeof item.recommendation_template_id === 'number';
        const hasPersistedActionPlan = typeof item.action_plan_id === 'number';
        const detailPath = canViewDetails
          ? `/app/mobile/focus-areas/recommendation/${item.recommendation_template_id}/${item.task_index ?? 0}`
          : undefined;
        const actionsPath = canViewDetails
          ? `/app/mobile/focus-areas/recommendation/${item.recommendation_template_id}/${item.task_index ?? 0}/actions`
          : undefined;

        return (
          <Card
            key={key}
            className={hasTemplate ? undefined : 'no-template'}
          >
            <CardHeader>
              <CardTitleRow>
                <Star className="w-4 h-4 text-amber-400" />
                <CardTitle>{itemTitle}</CardTitle>
              </CardTitleRow>
              <StyledTrashButton
                id={item.task_id ?? item.recommendation_template_id ?? key}
                onClick={(eventOrId: any) => {
                  if (eventOrId && typeof eventOrId.stopPropagation === 'function') {
                    eventOrId.stopPropagation();
                  }
                  setConfirmDeleteId(item.task_id ?? item.recommendation_template_id ?? key);
                }}
                title="Delete focus area"
              />
            </CardHeader>
            <CardDescription>{itemDescription}</CardDescription>
            <CardMeta>{item.softSkills || item.softSkills === '' ? `Soft skills: ${item.softSkills || 'Not specified'}` : 'Soft skills: Not specified'}</CardMeta>

            {!hasTemplate ? (
              <MissingTemplateBadge>
                <AlertTriangle size={12} />
                No template
              </MissingTemplateBadge>
            ) : null}

            {canViewDetails ? (
              <ActionButtonsRow>
                <StyledButton
                  label="View Details"
                  color="gray"
                  width="100%"
                  height="32px"
                  onClick={() => navigate(detailPath!, { state: { item, source: 'focusAreas' } })}
                />
                <StyledButton
                  label="Log my actions"
                  color="blue"
                  width="100%"
                  height="32px"
                  onClick={() => navigate(actionsPath!, { state: { item, source: 'focusAreas' } })}
                  disabled={!hasPersistedActionPlan}
                />
              </ActionButtonsRow>
            ) : null}
          </Card>
        );
      })}
      <Modal size="small" open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)} closeIcon>
        <Modal.Header>Confirm delete</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to remove this focus area? This can't be undone.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setConfirmDeleteId(null)}>
            Cancel
          </Button>
          <Button
            negative
            onClick={() => {
              if (confirmDeleteId !== null) {
                onRemove(confirmDeleteId);
              }
              setConfirmDeleteId(null);
            }}
          >
            Delete
          </Button>
        </Modal.Actions>
      </Modal>

      {items.some(it => !it.recommendation_template_id) && (
        <NoTemplateFooter>
          <AlertTriangle size={12} />
          {items.filter(it => !it.recommendation_template_id).length} recommendation{items.filter(it => !it.recommendation_template_id).length !== 1 ? 's' : ''} without templates will be skipped
        </NoTemplateFooter>
      )}
    </List>
  );
};

export default MobilePlannerTable;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const EmptyState = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 20px;
  color: #1f2937;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const EmptyStateBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyStateTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
`;

const List = styled.div`
  display: grid;
  gap: 14px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.no-template {
    background: #fffbeb;
    border-color: #fde68a;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const CardDescription = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.65;
`;

const CardMeta = styled.div`
  color: #64748b;
  font-size: 12px;
`;

const MissingTemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  font-size: 11px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 2px 8px;
`;

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
`;

const NoTemplateFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 10px 12px;
`;
